import { Autocomplete, TextField } from '@mui/material';
import { fieldToTextField, TextFieldProps } from 'formik-mui';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useCompaniesDropdown } from 'hooks/companies-dropdown.hook';
import { CompanyDto, CompanyTypeFilter } from '@sr/dto';

export const CompanyPickerField = (props: TextFieldProps & { legalEntityIdFilter?: number }) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    label,
    placeholder,
    legalEntityIdFilter
  } = props;
  
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  
  const { items: list, isLoading: fetching } = useCompaniesDropdown(inputValue, legalEntityIdFilter);
  
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(null);
  
  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: CompanyDto | null) => {
      setSelectedCompany(newValue);
      setFieldValue(name, newValue?.id);
    },
    [setFieldValue, name]
  );

  const onInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  useEffect(() => {
    if (value && list.length > 0) {
      const found = list.find((x) => x.id === value);
      if (found) {
        setSelectedCompany(found);
      }
    } else if (!value) {
      setSelectedCompany(null);
    }
  }, [list, value]);

  const uniqueOptions = Array.from(new Map(list.map(item => [item.id, item])).values());
  
  return (
    <Autocomplete
      value={selectedCompany}
      inputValue={inputValue}
      onInputChange={onInputChange}
      options={uniqueOptions}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      getOptionLabel={(option) => option?.name || ''}
      loading={fetching}
      loadingText={'Загрузка...'}
      noOptionsText={inputValue && !fetching ? 'Ничего не найдено' : 'Начните вводить название или ИНН...'}
      renderInput={(params) => (
        <TextField 
          {...fieldToTextField(props)} 
          {...params} 
          name={name} 
          label={label} 
          placeholder={placeholder || 'Начните вводить название или ИНН...'}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
        />
      )}
      filterOptions={(x) => x}
    />
  );
};

export const CompanyMultiPickerField = (props: TextFieldProps & {companyType?: CompanyTypeFilter}) => {
  const {
    form: { setFieldValue },
    field: { name, value = [] },
    label
  } = props;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { items: list, isLoading: fetching } = useCompaniesDropdown(inputValue, undefined, props.companyType);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: CompanyDto[]) => {
      setFieldValue(
        name,
        newValue.map((x) => x.id)
      );
    },
    [setFieldValue, name]
  );

  const isLoading = open && list.length === 0 && fetching;

  return (
    <Autocomplete
      multiple
      inputValue={inputValue}
      onInputChange={(event, newValue) => setInputValue(newValue)}
      options={list}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      getOptionLabel={(option) => option?.name || ''}
      loading={isLoading}
      loadingText={'Загрузка...'}
      noOptionsText={inputValue && !isLoading ? 'Ничего не найдено' : 'Начните вводить название или ИНН...'}
      renderInput={(params) => (
        <TextField 
          {...fieldToTextField(props)} 
          {...params} 
          name={name} 
          label={label} 
          placeholder="Начните вводить название или ИНН..."
        />
      )}
      filterOptions={(x) => x}
    />
  );
};